import { IStyle } from 'Styles';
import { ButtonsListContainer } from 'components/Button';
import { ImageSettings } from 'components/commandpanel/ExperimentPanel/ExperimentTools/ImageSettings';
import RMCustomButton, {
  RMCustomButtonProps,
} from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import ColorPicker from 'components/customComponents/Controls/ColorPicker';
import { useModalHandlers } from 'components/modal';
import {
  DrawingType,
  DrawingUpdateDetails,
  ROIDrawing,
  colorDrawingType,
  colorOverlayTools,
  lockDrawingType,
} from 'components/well/drawingManager/constants';
import { getRgbColorString } from 'components/well/drawingManager/utils';
import { RGBColor } from 'constants/';
import React, { useState } from 'react';
import { useRMDispatch } from 'redux/hooks';
import {
  useDrawingColor,
  useDropLocation,
  useSelectedDrawingTool,
  useSelectedWellDrawing,
} from 'redux/hooks/well';
import {
  moveDrawingToBack,
  setDrawingColor,
  updateDrawing,
} from 'redux/reducers/wellReducer';
import {
  deleteDrawing,
  setAutoDropLocation,
  toggleRoiImaging,
  updateDrawingThunk,
} from 'redux/thunks/well';
import AnnotationTools from './annotationTools';
import ImagingToggles, {
  ImagingToggleProps,
} from 'components/commandpanel/ExperimentPanel/ExperimentTools/ImagingTools';
import AnnotationToggles from 'components/commandpanel/ExperimentPanel/ExperimentTools/AnnotationToggles';
import DropImageSettings from 'components/commandpanel/ExperimentPanel/ExperimentTools/DropImageSettings';
import {
  useLastSelectedWellNavigationDetails,
  useSelectedDropImageParameters,
} from 'redux/hooks/experiment';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import useRMLite from 'CustomHooks/useRMLite';

const DrawingTools = () => {
  const selectedDrawing = useSelectedWellDrawing();
  const selectedDrawingCreationTool = useSelectedDrawingTool();
  const drawingColor = useDrawingColor();
  const { showModal, hideModal } = useModalHandlers();
  const dispatch = useRMDispatch();
  const [showImageSettingsModal, setImageSettingsModal] = useState(false);
  const isSnfRoute = window.location.pathname.includes('snf');
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const { wellNum, plateID } = wellNavigationDetails || {};
  const { dropNumber, regionName } = selectedDropImageParameters || {};
  const dropLocation = useDropLocation();
  const rmLite = useRMLite();

  const handleUpdateDrawing = (drawingUpdateDetails: DrawingUpdateDetails) => {
    dispatch(
      updateDrawing({
        id: selectedDrawing.drawingId,
        drawingType: selectedDrawing.drawingType,
        plateId: selectedDrawing.plateId,
        wellNumber: selectedDrawing.wellNumber,
        dropNumber: selectedDrawing.dropNumber,
        regionName: selectedDrawing.regionName,
        drawingUpdateDetails,
      }),
    );
  };

  const toolOptionButtons: RMCustomButtonProps[] = [
    {
      iconProps: {
        name: 'Delete',
        width: 24,
        height: 24,
        enableBackground: 'true',
        fill: 'white',
        className: 'bg-red-500 rounded-sm p-[3px]',
      },
      isDisabled: selectedDrawing?.drawing ? false : true,
      onClick: () => {
        dispatch(deleteDrawing(selectedDrawing));
      },
    },
  ];

  const roiToolOptions: RMCustomButtonProps[] = [
    {
      iconProps: { name: 'ImageSettings' },
      isDisabled:
        selectedDrawing?.drawingType !== DrawingType.ROI ||
        !(selectedDrawing?.drawing as ROIDrawing)?.imagingEnabled,
      onClick: () => {
        setImageSettingsModal(true);
      },
    },
  ];

  const currentDrawingColor = selectedDrawing?.drawing?.['color']
    ? getRgbColorString(selectedDrawing?.drawing?.['color'])
    : getRgbColorString(drawingColor);
  const isLockOn =
    selectedDrawing &&
    lockDrawingType.includes(selectedDrawing.drawingType) &&
    selectedDrawing?.drawing?.['locked'];
  const isWellImagingEnabled =
    selectedDrawing?.drawingType !== DrawingType.ROI ||
    !(selectedDrawing?.drawing as ROIDrawing)?.imagingEnabled;

  const lockToggle: ImagingToggleProps[] = [
    {
      label: 'Lock',
      isOn: isLockOn,
      onToggle: () => handleUpdateDrawing({ locked: !isLockOn }),
      disabled:
        selectedDrawing?.drawingType !== DrawingType.ROI &&
        selectedDrawing?.drawingType !== DrawingType.POI,
    },
  ];

  const roiToggle: ImagingToggleProps[] = [
    {
      label: 'Disable ROI',
      isOn:
        selectedDrawing?.drawingType === DrawingType.ROI ? isWellImagingEnabled : false,
      onToggle: () =>
        dispatch(
          toggleRoiImaging({
            drawingId: selectedDrawing.drawingId,
            drawingType: selectedDrawing.drawingType,
            plateId: selectedDrawing.plateId,
            wellNumber: selectedDrawing.wellNumber,
            dropNumber: selectedDrawing.dropNumber,
            regionName: selectedDrawing.regionName,
            drawingUpdateDetails: {
              imagingEnabled:
                (selectedDrawing?.drawing as ROIDrawing).imagingEnabled === true
                  ? false
                  : true,
            },
          }),
        ),
      disabled:
        selectedDrawing?.drawingType !== DrawingType.ROI ||
        selectedDrawing?.drawingId <= 0,
    },
  ];

  const handleAutoDropLocation = async () => {
    dispatch(
      setAutoDropLocation({
        plateId: plateID,
        wellNumber: wellNum,
        dropNumber: dropNumber,
        regionName: regionName,
      }),
    );
  };

  return (
    <div id="drawingtools-main-container" className="mb-3">
      <AnnotationTools selectedDrawing={selectedDrawing} />
      <div id="drawingtools-options-wrapper" style={styles.tools}>
        <div id="drawingtools-options-colorpicker-title" style={styles.toolsHeading}>
          Color Picker
        </div>
        <div id="drawingtools-options-colorpicker-button" style={styles.toolsContent}>
          <RMCustomButton
            iconProps={{
              name: 'ColorPicker',
              fill: currentDrawingColor,
            }}
            isDisabled={
              !(
                selectedDrawing && colorDrawingType.includes(selectedDrawing.drawingType)
              ) &&
              !(
                selectedDrawingCreationTool &&
                colorOverlayTools.includes(selectedDrawingCreationTool)
              )
            }
            onClick={() => {
              showModal({
                body: (
                  <ColorPicker
                    onChangeColor={(color: RGBColor) => {
                      if (selectedDrawing) {
                        dispatch(
                          updateDrawingThunk({
                            drawingId: selectedDrawing.drawingId,
                            drawingType: selectedDrawing.drawingType,
                            plateId: selectedDrawing.plateId,
                            wellNumber: selectedDrawing.wellNumber,
                            dropNumber: selectedDrawing.dropNumber,
                            regionName: selectedDrawing.regionName,
                            drawingUpdateDetails: {
                              color: `${color.r},${color.g},${color.b}`,
                            },
                          }),
                        );
                      } else {
                        dispatch(setDrawingColor(color));
                      }
                    }}
                    setShowModal={hideModal}
                    color={currentDrawingColor}
                  />
                ),
                showButtons: false,
                heading: 'Select Color',
              });
            }}
            customStyle={{
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: '#000000',
            }}
            contentContainerClass="p-1 rounded-sm"
            showChevronIcon
          />
        </div>
        <div
          id="drawingtools-options-arrangeoptions-title"
          style={{ ...styles.toolsHeading, paddingRight: '0%' }}
        >
          Arrange Options
        </div>
        {!rmLite && (
          <div
            id="drawingtools-options-arrangeoptions-button"
            style={styles.toolsContent}
          >
            <RMCustomButton
              Icon={
                <RMSvgIcon id="drawtools-moveback-icon"
                  name={'MoveBack'}
                  width={20}
                  height={20}
                  enableBackground={'true'}
                />
              }
              isDisabled={selectedDrawing ? false : true}
              onClick={() => {
                if (selectedDrawing) {
                  dispatch(
                    moveDrawingToBack({
                      drawingId: selectedDrawing.drawingId,
                      drawingType: selectedDrawing.drawingType,
                      plateId: selectedDrawing.plateId,
                      wellNumber: selectedDrawing.wellNumber,
                      dropNumber: selectedDrawing.dropNumber,
                      regionName: selectedDrawing.regionName,
                    }),
                  );
                }
              }}
              customStyle={{ height: 'fit-content' }}
            />
          </div>
        )}
        <div id="drawingtools-options-tools-content-buttons" style={styles.toolsContent}>
          <ButtonsListContainer
            containerStyleClass="align-items-center"
            buttonsList={toolOptionButtons}
          />
        </div>
        <ImagingToggles toggles={lockToggle} />
      </div>
      {!isSnfRoute && (
        <div
          id="drawingtools-tools-drop-image-settings"
          className="flex w-full"
          style={styles.tools}
          aria-disabled
        >
          <DropImageSettings
            customizeSettingsClassName="multilevel-submenu top-12 right-1"
            disabled={rmLite}
          />
        </div>
      )}
      <div id="drawingtools-tools-imaging-roi-wrapper" style={styles.tools}>
        <div
          id="drawingtools-tools-imaging-roi-title"
          style={styles.imageSettingsHeading}
        >
          Imaging ROI
        </div>
        <div id="drawingtools-tools-imaging-roi-options" style={styles.imageSettings}>
          <ButtonsListContainer buttonsList={roiToolOptions} marginBetweenItems={5} />
        </div>
        <ImagingToggles toggles={roiToggle} />
        <ImageSettings
          setShowModal={setImageSettingsModal}
          showModal={showImageSettingsModal}
          roiId={
            selectedDrawing?.drawingType === DrawingType.ROI && selectedDrawing?.drawingId
          }
        ></ImageSettings>
      </div>
      {!isSnfRoute && (
        <div id="drawingtools-annotationtoggles" className="mt-2 mr-[30px] ml-[1%]">
          <AnnotationToggles />
        </div>
      )}
      <div id="drawingtools-drop-location" className="mt-2 mx-[30px]">
        Drop Location{' '}
        <button
          id="drawingtools-drop-location-button"
          className="uppercase border-0 bg-black text-white disabled:opacity-50"
          onClick={handleAutoDropLocation}
          disabled={!dropLocation?.length}
        >
          Set Auto Location
        </button>
      </div>
    </div>
  );
};

export default DrawingTools;

const styles: IStyle = {
  tools: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  toolsHeading: {
    paddingBlock: '2%',
    paddingInline: '1%',
    paddingRight: '2%',
  },
  toolsContent: {
    display: 'flex',
    paddingBlock: '2%',
    paddingInline: '2%',
    alignItems: 'center',
  },
  toolsButton: {
    height: 'fit-content',
    paddingLeft: '5%',
  },
  imageSettingsHeading: {
    paddingBlock: '2%',
    paddingInline: '1%',
    paddingRight: '6%',
  },
  imageSettings: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '10%',
  },
};
